import * as api from '../../api';
import * as sharingLinkActions from './actions';
import { message } from '@myua/kit';
import * as routerActions from '../router/actions';
import { Page } from '../router/model';
import { getPage } from '../router/selectors';
import {
  getSelectedIds,
  getSorter,
  getPagination,
  getSearchQuery,
  getIsActiveFilter,
} from './selectors';
import { getAssetLabLogger } from '../app/logs';
import { takeLatest, takeEvery } from '../../listenerMiddleware';

export const sharingLinkListeners = () => {
  watchPagePathChanged();
  watchLoadLinksRequested();
  watchDeleteClicked();
  watchActivateClicked();
  watchSubmitClicked();
  watchReload();
};

function watchPagePathChanged() {
  takeEvery(routerActions.pagePathChanged, (action, { getState, dispatch }) => {
    const page = getPage(getState());
    if (page !== Page.sharingLinks) {
      return;
    }
    dispatch(sharingLinkActions.loadLinksRequested());
  });
}

function watchLoadLinksRequested() {
  takeLatest(
    sharingLinkActions.loadLinksRequested,
    async (action, { getState, dispatch, signal }) => {
      const sorter = getSorter(getState());
      const pagination = getPagination(getState());
      const searchQuery = getSearchQuery(getState());
      const isActiveFilter = getIsActiveFilter(getState());

      dispatch(sharingLinkActions.setLoading({ loading: true }));
      // Фильтрация и пагинация серверные: is_active, page, search и ordering уходят
      // в API, total считается на бэке (MYUA-16466).
      const result = await api.loadSharingLinks({
        ordering:
          sorter.columnKey && sorter.order
            ? `${sorter.order === 'descend' ? '-' : ''}${sorter.columnKey}`
            : undefined,
        page: pagination.current,
        search: searchQuery,
        is_active: isActiveFilter,
      });
      if (signal.aborted) {
        return;
      }
      dispatch(sharingLinkActions.setLoading({ loading: false }));

      dispatch(sharingLinkActions.linksSelected({ ids: [] }));

      if (result.success) {
        if (result.data.response.items.length === 0 && pagination.current !== 1) {
          dispatch(sharingLinkActions.paginationChanged({ current: 1 }));
        } else {
          dispatch(
            sharingLinkActions.linksChanged({
              links: result.data.response.items,
              total: result.data.response.total,
            }),
          );
        }
      } else {
        void message.error('Links have not been loaded');
        dispatch(sharingLinkActions.linksChanged({ links: [], total: 0 }));
      }
    },
  );
}

function watchDeleteClicked() {
  takeLatest(sharingLinkActions.deleteClicked, async (action, { getState, dispatch, signal }) => {
    const selectedIds = getSelectedIds(getState());

    const assetLabLogger = getAssetLabLogger(getState());
    assetLabLogger({
      action: 'Sharing',
      sub_action: 'Delete links',
      // extra: { sharing_ids: selectedIds },
    });

    const messageKey = 'sharingLink.delete';
    void message.loading({ key: messageKey, content: 'Processing...' });

    const result = await api.deleteSharingLinks(selectedIds);
    if (signal.aborted) {
      return;
    }
    if (result.success) {
      void message.success({ key: messageKey, content: 'Links have been deactivated' });
      dispatch(sharingLinkActions.loadLinksRequested());
    } else {
      void message.error({ key: messageKey, content: 'Links have not been deactivated' });
    }
  });
}

function watchActivateClicked() {
  takeLatest(sharingLinkActions.activateClicked, async (action, { getState, dispatch, signal }) => {
    const selectedIds = getSelectedIds(getState());

    const assetLabLogger = getAssetLabLogger(getState());
    assetLabLogger({
      action: 'Sharing',
      sub_action: 'Restore links',
    });

    const messageKey = 'sharingLink.activate';
    void message.loading({ key: messageKey, content: 'Processing...' });

    const result = await api.restoreSharingLinks(selectedIds);
    if (signal.aborted) {
      return;
    }
    if (result.success) {
      void message.success({ key: messageKey, content: 'Links have been activated' });
      dispatch(sharingLinkActions.loadLinksRequested());
    } else {
      void message.error({ key: messageKey, content: 'Links have not been activated' });
    }
  });
}

function watchSubmitClicked() {
  takeLatest(
    sharingLinkActions.linkModalSubmitted,
    async ({ payload }, { getState, dispatch, signal }) => {
      dispatch(sharingLinkActions.linkModalSetInProgress({ inProgress: true }));

      const assetLabLogger = getAssetLabLogger(getState());
      assetLabLogger({
        action: 'Sharing',
        sub_action: 'Edit link',
        extra: { text_1: payload.id, text_2: payload.name },
      });

      const result = await api.updateSharingLink(payload);
      if (signal.aborted) {
        return;
      }
      dispatch(sharingLinkActions.linkModalSetInProgress({ inProgress: false }));

      if (result.success) {
        dispatch(sharingLinkActions.linkModalSetVisible({ visible: false }));
        dispatch(sharingLinkActions.linkChanged({ link: result.data.response }));
        void message.success('Data has been updated');
      } else {
        void message.error('Data has not been updated');
      }
    },
  );
}

function watchReload() {
  takeEvery(
    [
      sharingLinkActions.sorterChanged,
      sharingLinkActions.paginationChanged,
      sharingLinkActions.searchQueryChanged,
      sharingLinkActions.isActiveFilterChanged,
    ],
    (action, { dispatch }) => {
      dispatch(sharingLinkActions.loadLinksRequested());
    },
  );
}
